import React, { useState, useEffect } from 'react';
import {
  Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useTranslate } from './TranslationText';
import { getHorseEnduranceResults } from '../pages/sharedAPI/lookUpApis';
import { GoldMedalSvg, SilverMedalSvg, BronzeMedalSvg } from './svg';
import { formatDateTimeToString } from '../helper/formatDateTimeToString';



const MedalIcon = ({ index, position }) => {
  const pos = position !== undefined ? parseInt(position, 10) : (index !== undefined ? index + 1 : 0);
  if (pos === 1) {
    return <GoldMedalSvg width="46" height="43" />;
  }
  if (pos === 2) {
    return <SilverMedalSvg width="46" height="43" />;
  }
  if (pos === 3) {
    return <BronzeMedalSvg width="46" height="43" />;
  }
  return <i className="fa-solid fa-medal" style={{ fontSize: '24px', color: '#718096' }}></i>;
};

export default function EnduranceRidesTab({ horseId, initialRides }) {
  const { translate } = useTranslate();
  const reduxLang = useSelector((state) => state.studBookSlice?.currentLanguage);
  const currentLanguage = reduxLang || (typeof window !== 'undefined' ? localStorage.getItem('eahsLang') : null) || 'en';
  const isRtl = currentLanguage === 'ar';
  const [rides, setRides] = useState(initialRides || []);
  const [loading, setLoading] = useState(false);
  const [expandedRide, setExpandedRide] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (horseId) {
      setLoading(true);
      getHorseEnduranceResults(horseId)
        .then((data) => {
          if (isMounted) {
            setRides(Array.isArray(data) ? data : []);
            setExpandedRide(null);
            setLoading(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            setRides([]);
            setLoading(false);
          }
        });
    } else if (!initialRides) {
      setRides([]);
      setLoading(false);
    }
    return () => {
      isMounted = false;
    };
  }, [horseId, initialRides]);

  const colors = {
    bg: '#F8F9FA',
    cardBg: '#FFFFFF',
    border: '#E8ECEF',
    textMain: '#1A202C',
    textMuted: '#718096',
    primary: '#D31D36',
    gold: '#FFBB0D',
    goldBorder: '#E89B05',
    silver: '#81A5B0',
    silverBorder: '#BCCCD2',
    bronze: '#C38144',
    bronzeBorder: '#DCA16A',
    chartTime: '#7B7979',
    chartHeart: '#0B9444',
    chartSpeed: '#D31D36'
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`,
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          textAlign: isRtl ? 'right' : 'left',
          direction: isRtl ? 'rtl' : 'ltr'
        }}>
          <p style={{ color: colors.textMain, margin: '0 0 8px 0', fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry, index) => {
            let displayVal = entry.value;
            if (typeof entry.value === 'number') {
              if (entry.dataKey === 'speed') {
                displayVal = entry.value.toFixed(2);
              } else if (entry.dataKey === 'totalTime') {
                displayVal = Math.round(entry.value);
              }
            }
            return (
              <p key={index} style={{ color: entry.color, margin: 0, fontSize: '13px', fontWeight: '500' }}>
                {entry.name}: {displayVal}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="text-center w-100 py-5">
        <i className="fas fa-spinner fa-spin fa-3x color-eahsred"></i>
      </div>
    );
  }

  if (!rides || rides.length === 0) {
    return (
      <div className="text-center py-5" style={{ color: colors.textMuted }}>
        <i className="fa-solid fa-flag-checkered mb-3" style={{ fontSize: '36px', opacity: 0.4 }}></i>
        <p style={{ fontSize: '15px', fontWeight: '500' }}>
          {translate("web.noEnduranceRidesFound", isRtl ? "لم يتم العثور على نتائج سباقات القدرة لهذا الخيل." : "No endurance ride results found for this horse.")}
        </p>
      </div>
    );
  }

  return (
    <div
      className={isRtl ? 'rtl' : ''}
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{
        color: colors.textMain,
        fontFamily: isRtl ? 'inherit' : "'Inter', sans-serif",
        textAlign: isRtl ? 'right' : 'left',
        direction: isRtl ? 'rtl' : 'ltr'
      }}
    >
      <div className={`py-2 ${isRtl ? 'rtl' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
        {rides.map((ride, index) => {
          const isExpanded = expandedRide === index;
          const posNum = parseInt(ride.finalPosition, 10);
          const hasMedal = posNum >= 1 && posNum <= 3;

          const title = (currentLanguage === 'ar' && ride.raceNameAr)
            ? ride.raceNameAr
            : (ride.raceName || translate("web.enduranceRace", isRtl ? "سباق قدرة" : "Endurance Race"));

          const formattedDate = ride.raceStartTime
            ? formatDateTimeToString(ride.raceStartTime)
            : '-';

          const gates = Array.isArray(ride.gates) ? ride.gates : [];
          const lastGate = gates.length > 0 ? gates[gates.length - 1] : null;
          const parseSpeed = (val) => {
            if (!val) return null;
            const num = parseFloat(val);
            return !isNaN(num) && num > 0 ? `${num.toFixed(2)} ${translate("web.kmh", isRtl ? "كم/س" : "km/h")}` : null;
          };
          const overallSpeed = parseSpeed(lastGate?.averageSpeed) || parseSpeed(lastGate?.speed) || parseSpeed(lastGate?.loopAverageSpeed) || parseSpeed(lastGate?.loopSpeed);

          const chartData = gates.map((gate, i) => {
            const dist = parseFloat(gate.loopDistance || 0);
            const spd = parseFloat(gate.speed || gate.loopSpeed || gate.averageSpeed || 0);
            const hr = parseInt(gate.heartRate || 0, 10);
            const rawTimeInMin = gate.timeInMin ?? gate.TimeInMin;
            const parsedTime = parseFloat(rawTimeInMin);
            const totalTime = (!isNaN(parsedTime) && parsedTime > 0)
              ? Math.round(parsedTime)
              : ((dist > 0 && spd > 0) ? Math.round((dist / spd) * 60) : 0);
            return {
              name: `L${i + 1}`,
              speed: spd,
              heartRate: hr,
              totalTime: totalTime,
            };
          });

          const isReplacement = Boolean(ride.isReplacementHorse || ride.replacementHorse);

          return (
            <div
              key={ride.raceId || index}
              className="mb-3"
              style={{
                backgroundColor: colors.cardBg,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <div>
                <div
                  className="p-3 p-md-4"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                    transition: 'all 0.2s ease',
                    direction: isRtl ? 'rtl' : 'ltr'
                  }}
                  onClick={() => setExpandedRide(isExpanded ? null : index)}
                >
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem' }}>
                    {hasMedal ? (
                      <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '46px', height: '43px' }}>
                        <MedalIcon position={posNum} />
                      </div>
                    ) : (
                      <div
                        className="text-center rounded d-flex flex-column align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: '46px',
                          height: '43px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E2E8F0'
                        }}
                      >
                        <div style={{ fontSize: '9px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 'bold', lineHeight: '1' }}>
                          {translate("web.pos", isRtl ? "المركز" : "Pos")}
                        </div>
                        <div className="fw-bold fs-5" style={{ color: colors.textMain, lineHeight: '1', marginTop: '2px' }}>
                          {ride.finalPosition || ride.serial || (index + 1)}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <h5 style={{ color: colors.textMain, fontWeight: '700', margin: 0, fontSize: '16px', textAlign: isRtl ? 'right' : 'left' }}>
                          {title}
                        </h5>
                        {isReplacement && (
                          <span className="badge bg-secondary text-white rounded-pill px-2 py-1" style={{ fontSize: '11px', fontWeight: '500' }}>
                            {translate("web.replacementHorse", isRtl ? "خيل بديل" : "Replacement Horse")}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                        {formattedDate !== '-' && (
                          <span style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <i className="fa-regular fa-calendar w-unset" style={{ color: 'var(--description, #838383)', width: 'auto' }}></i>
                            <strong style={{ color: colors.textMain, fontWeight: '700' }}>
                              {translate("web.date", isRtl ? "التاريخ" : "Date")}:
                            </strong>
                            <span style={{ color: 'var(--description, #838383)', fontWeight: '500' }}>{formattedDate}</span>
                          </span>
                        )}
                        {overallSpeed && (
                          <span style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <i className="fa-solid fa-gauge-high w-unset" style={{ color: 'var(--description, #838383)', width: 'auto' }}></i>
                            <strong style={{ color: colors.textMain, fontWeight: '700' }}>
                              {translate("web.speed", isRtl ? "السرعة" : "Speed")}:
                            </strong>
                            <span style={{ color: 'var(--description, #838383)', fontWeight: '500' }}>{overallSpeed}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.5rem', height: '100%' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(211, 29, 54, 0.25)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                        <polyline points={isExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
                      </svg>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: '#fff' }}>

                    {/* Chart Section */}
                    {chartData.length > 0 && (
                      <div className="p-4 p-md-5">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 style={{ fontWeight: '700', color: colors.textMain, margin: 0, textAlign: isRtl ? 'right' : 'left', width: '100%' }}>
                            {translate("web.performanceAcrossLoops", isRtl ? "الأداء عبر الأشواط" : "Performance Across Loops")}
                          </h5>
                        </div>

                        <div style={{ width: '100%', height: 320, direction: 'ltr' }}>
                          <ResponsiveContainer>
                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                              <defs>
                                <linearGradient id={`colorTime_${index}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={colors.chartTime} stopOpacity={0.2} />
                                  <stop offset="95%" stopColor={colors.chartTime} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id={`colorHeart_${index}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={colors.chartHeart} stopOpacity={0.2} />
                                  <stop offset="95%" stopColor={colors.chartHeart} stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDF2F7" />
                              <XAxis dataKey="name" tick={{ fill: colors.textMuted, fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false} dy={10} />
                              <YAxis tick={{ fill: colors.textMuted, fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false} dx={-10} />
                              <Tooltip content={<CustomTooltip />} />
                              <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '13px', color: colors.textMuted, fontWeight: 500 }} />

                              <Area
                                type="monotone"
                                dataKey="totalTime"
                                name={translate("web.totalTimeMin", isRtl ? "إجمالي الوقت (دقيقة)" : "Total Time (min)")}
                                stroke={colors.chartTime}
                                strokeWidth={3}
                                fillOpacity={1}
                                fill={`url(#colorTime_${index})`}
                                activeDot={{ r: 6, fill: colors.chartTime, stroke: '#fff', strokeWidth: 2 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="heartRate"
                                name={translate("web.heartRateBpm", isRtl ? "معدل ضربات القلب (نبضة/د)" : "Heart Rate (bpm)")}
                                stroke={colors.chartHeart}
                                strokeWidth={3}
                                fillOpacity={1}
                                fill={`url(#colorHeart_${index})`}
                                activeDot={{ r: 6, fill: colors.chartHeart, stroke: '#fff', strokeWidth: 2 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="speed"
                                name={translate("web.avgSpeedKmH", isRtl ? "متوسط السرعة (كم/س)" : "Avg Speed (km/h)")}
                                stroke={colors.chartSpeed}
                                strokeWidth={3}
                                dot={{ r: 4, fill: '#fff', stroke: colors.chartSpeed, strokeWidth: 2 }}
                                activeDot={{ r: 6 }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Standardized Data Table */}
                    <div className="px-4 pb-5">
                      <div className="scrolableTable mx-auto">
                        <div className="row mx-0">
                          <table className={`table tablenominwidth table-striped table-bordered horseResultTable ${isRtl ? 'rtl' : ''}`}>
                            <thead className="theadHorseSelect">
                              <tr>
                                <th className="text-center">{translate("web.gate", isRtl ? "المرحلة" : "GATE")}</th>
                                <th className="text-center">{translate("web.distanceKm", isRtl ? "المسافة (كم)" : "DISTANCE (KM)")}</th>
                                <th className="text-center">{translate("web.speedKmH", isRtl ? "السرعة (كم/س)" : "SPEED (KM/H)")}</th>
                                <th className="text-center">{translate("web.recoveryTime", isRtl ? "وقت التعافي" : "RECOVERY TIME")}</th>
                                <th className="text-center">{translate("web.heartRate", isRtl ? "معدل ضربات القلب" : "HEART RATE")}</th>
                              </tr>
                            </thead>
                            <tbody className="text-center">
                              {gates.length > 0 ? (
                                gates.map((row, i) => {
                                  const distance = parseFloat(row.loopDistance || 0);
                                  const speed = parseFloat(row.speed || row.loopSpeed || row.averageSpeed || 0);
                                  return (
                                    <tr key={row.id || row.raceGateId || i}>
                                      <td>
                                        <span className="fw-bold">L{i + 1}</span>
                                      </td>
                                      <td>{distance > 0 ? distance.toFixed(1) : '-'}</td>
                                      <td>{speed > 0 ? speed.toFixed(2) : '-'}</td>
                                      <td>{row.recovery || '-'}</td>
                                      <td>
                                        {row.heartRate ? (
                                          <>
                                            <span className="fw-bold">{row.heartRate}</span>{" "}
                                            <span style={{ fontSize: '10px', color: colors.textMuted }}>
                                              {translate("web.bpm", isRtl ? "نبضة/د" : "BPM")}
                                            </span>
                                          </>
                                        ) : '-'}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center py-3 text-muted">
                                    {translate("web.noLoopDetailsAvailable", isRtl ? "لا تتوفر تفاصيل المراحل" : "No loop details available")}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                  </div>
                )}

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

